import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sourceLabel, sourceBadgeClass } from "@/lib/shelterUpdateSource";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, Clock, Check, X, ChevronDown, History as HistoryIcon, ArrowRight, MapPin, AlertTriangle, Users } from "lucide-react";
import { format } from "date-fns";
import { pl } from "date-fns/locale";

const FIELD_LABELS: Record<string, string> = {
  name: "Nazwa",
  shelter_name: "Nazwa",
  address: "Adres",
  postal_code: "Kod pocztowy",
  city: "Miasto",
  region: "Województwo",
  lat: "Szerokość geo.",
  lng: "Długość geo.",
  phone: "Telefon",
  email: "E-mail",
  website: "Strona WWW",
  description: "Opis",
  current_needs: "Aktualne potrzeby",
  urgent_needs: "Pilne potrzeby",
  volunteer_info: "Info o wolontariacie",
  volunteering_available: "Wolontariat dostępny",
  dog_walking_available: "Wyprowadzanie psów",
  dogs_available: "Psy do adopcji",
  cats_available: "Koty do adopcji",
};

const STATUS_LABELS: Record<string, string> = {
  approved: "Zatwierdzone",
  pending: "Oczekuje",
  rejected: "Odrzucone",
};

type HistoryEntry = {
  id: string;
  created_at: string;
  reviewed_at: string | null;
  source: string;
  status: string;
  changed_fields: string[] | null;
  is_self: boolean;
  event_type: string | null;
  event_label: string | null;
  original_values: Record<string, unknown> | null;
  new_values: Record<string, unknown> | null;
  admin_note: string | null;
  actor_email: string | null;
};

const formatValue = (key: string, value: unknown): string => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Tak" : "Nie";
  if (key === "lat" || key === "lng") {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(5) : String(value);
  }
  return String(value);
};

const PanelHistory = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const { data: shelter, isLoading: loadingShelter } = useQuery({
    queryKey: ["panel-shelter", slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("shelters")
        .select("id, name, city, slug")
        .eq("slug", slug!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const shelterId = shelter?.id;

  const { data: history, isLoading: loadingHistory } = useQuery({
    queryKey: ["panel-history-full", shelterId],
    enabled: !!shelterId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_owner_shelter_history", {
        _shelter_id: shelterId!,
        _limit: 100,
      });
      if (error) throw error;
      return (data ?? []) as unknown as HistoryEntry[];
    },
  });

  const isLoading = loadingShelter || loadingHistory;

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const entries = useMemo(() => history ?? [], [history]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!shelter) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center text-muted-foreground">
        <p>Nie znaleziono schroniska.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/panel")}>
          Wróć do pulpitu
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" />
          <span className="truncate">{shelter.name}{shelter.city ? ` - ${shelter.city}` : ""}</span>
        </div>
        <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Historia zmian</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Wszystkie zmiany danych profilu, akcje administratora oraz zmiany w zespole. Kliknij wpis, aby zobaczyć co konkretnie się zmieniło.
        </p>
      </div>

      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <HistoryIcon className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="font-medium">Brak historii zmian</p>
            <p className="text-sm">Tutaj pojawią się aktualizacje profilu i działania administratora.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {entries.map((entry) => {
            const isOpen = expanded.has(entry.id);
            const isUpdate = entry.event_type === "shelter_update";
            const changed = entry.changed_fields ?? [];
            const hasDiff = isUpdate && changed.length > 0;
            const hasReviewedDifferentFromCreated =
              !!entry.reviewed_at && entry.reviewed_at !== entry.created_at;
            const hasExpandableContent =
              hasDiff ||
              isUpdate ||
              !!entry.admin_note ||
              hasReviewedDifferentFromCreated;
            const title = entry.event_label ?? sourceLabel(entry.source);

            const headerIcon = isUpdate
              ? entry.status === "approved"
                ? <Check className="h-4 w-4 text-emerald-600" />
                : entry.status === "rejected"
                  ? <X className="h-4 w-4 text-red-600" />
                  : <Clock className="h-4 w-4 text-amber-600" />
              : <Users className="h-4 w-4 text-primary" />;

            return (
              <Card key={entry.id} className="overflow-hidden">
                <Collapsible
                  open={hasExpandableContent && isOpen}
                  onOpenChange={() => hasExpandableContent && toggle(entry.id)}
                >
                  <CollapsibleTrigger asChild disabled={!hasExpandableContent}>
                    <button
                      type="button"
                      disabled={!hasExpandableContent}
                      className={`w-full text-left transition-colors ${
                        hasExpandableContent ? "hover:bg-muted/40 cursor-pointer" : "cursor-default"
                      }`}
                    >
                      <CardHeader className="p-3 sm:p-4">
                        <div className="flex items-start gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                            {headerIcon}
                          </span>
                          <div className="flex-1 min-w-0 space-y-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <CardTitle className="text-sm font-semibold leading-tight">
                                {title}
                              </CardTitle>
                              {isUpdate && (
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 ${sourceBadgeClass(entry.source)}`}>
                                  {sourceLabel(entry.source)}
                                </Badge>
                              )}
                              {isUpdate && entry.status !== "approved" && (
                                <Badge variant={entry.status === "rejected" ? "destructive" : "secondary"} className="text-[10px] px-1.5 py-0 h-5">
                                  {STATUS_LABELS[entry.status] ?? entry.status}
                                </Badge>
                              )}
                              {hasDiff && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 text-muted-foreground">
                                  {changed.length} {changed.length === 1 ? "zmiana" : changed.length < 5 ? "zmiany" : "zmian"}
                                </Badge>
                              )}
                            </div>
                            <CardDescription className="text-xs">
                              {format(new Date(entry.created_at), "d MMM yyyy, HH:mm", { locale: pl })}
                              {entry.is_self && <span className="ml-2 text-primary">(Ty)</span>}
                              {hasDiff && (
                                <span className="ml-2 truncate">
                                  - {changed.slice(0, 4).map((f) => FIELD_LABELS[f] ?? f).join(", ")}
                                  {changed.length > 4 && ` +${changed.length - 4}`}
                                </span>
                              )}
                            </CardDescription>
                            {entry.actor_email && !isUpdate && (
                              <div className="text-xs text-muted-foreground truncate">
                                <span className="font-medium text-foreground/80">Osoba:</span> {entry.actor_email}
                              </div>
                            )}
                          </div>
                          {hasExpandableContent && (
                            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform shrink-0 mt-1 ${isOpen ? "rotate-180" : ""}`} />
                          )}
                        </div>
                      </CardHeader>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="p-3 sm:p-4 pt-0 space-y-3">
                      {hasDiff ? (
                        <div className="rounded-md border bg-muted/30 divide-y">
                          {changed.map((key) => {
                            const before = entry.original_values?.[key];
                            const after = entry.new_values?.[key];
                            return (
                              <div key={key} className="p-2.5 grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-1 sm:gap-3 text-xs">
                                <div className="font-medium text-foreground">
                                  {FIELD_LABELS[key] ?? key}
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2 min-w-0">
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Przed</div>
                                    <div className="text-muted-foreground line-through break-words">
                                      {formatValue(key, before)}
                                    </div>
                                  </div>
                                  <ArrowRight className="hidden sm:block h-3.5 w-3.5 text-muted-foreground shrink-0 mt-4" />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5">Po</div>
                                    <div className="font-medium text-foreground break-words">
                                      {formatValue(key, after)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : isUpdate ? (
                        <p className="text-xs text-muted-foreground">
                          Brak szczegółów o zmienionych polach (starszy wpis lub utworzenie profilu).
                        </p>
                      ) : null}

                      {entry.reviewed_at && entry.reviewed_at !== entry.created_at && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <Check className="h-3 w-3" />
                          Rozpatrzono: {format(new Date(entry.reviewed_at), "d MMM yyyy, HH:mm", { locale: pl })}
                        </div>
                      )}

                      {entry.admin_note && (
                        <div className="text-xs p-2 rounded bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/40 text-amber-900 dark:text-amber-100">
                          <span className="font-medium">Notatka administratora:</span> {entry.admin_note}
                        </div>
                      )}

                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PanelHistory;
